import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedTime } from '../components/FormattedDate';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';
import './AttendanceCalendar.css';

const AttendanceCalendar = ({
     days,
     monthRange,
     attendanceSummary,
     holidays = [],
     onMonthChange,
     weekOffDay,
     employeeType = "Contractual_Employee",
     onRegularizationRequest = () => { },
     onApplyLeave = () => { }
}) => {
     const navigate = useNavigate();
     const [optionsModal, setOptionsModal] = useState({ show: false, dateStr: null, dayNumber: null, position: {} });
     const [hoveredDay, setHoveredDay] = useState(null);
     const [popupDetails, setPopupDetails] = useState({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     const [sundaysCount, setSundaysCount] = useState(0);
     const [holidaysCount, setHolidaysCount] = useState(0);
     const [selectedMonth, setSelectedMonth] = useState("");
     const [attendanceStrategy, setAttendanceStrategy] = useState(null);

     const handleCellClick = (e, dateStr, dayNumber) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          setOptionsModal({
               show: true,
               dateStr,
               dayNumber,
               position: {
                    top: rect.bottom + window.scrollY + 5,
                    left: rect.left + window.scrollX,
               }
          });
     };
     const closeOptionsModal = () => setOptionsModal({ show: false, dateStr: null, dayNumber: null, position: {} });

     const handleRegularization = () => {
          // allow parent prop callback (if any)
          if (onRegularizationRequest) onRegularizationRequest(optionsModal.dateStr);
          closeOptionsModal();
          // navigate to the form, pass date via state (RegularizeRequestForm reads it)
          navigate('/regularize-request', { state: { date: optionsModal.dateStr } });
     };
     const handleApplyLeave = () => {
          if (onApplyLeave) onApplyLeave(optionsModal.dateStr);
          closeOptionsModal();
     };

     const dayNameToNumber = {
          Sunday: 0,
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
     };
     const weekOffDayNumber = dayNameToNumber[weekOffDay] ?? 0;

     useEffect(() => {
          setSelectedMonth(
               `${new Date(monthRange.start).getFullYear()}-${String(new Date(monthRange.start).getMonth() + 1).padStart(2, '0')}`
          );
     }, [monthRange]);

     useEffect(() => {
          try {
               const strategy = AttendanceStrategyFactory.getStrategy(employeeType);
               setAttendanceStrategy(strategy);
          } catch {
               setAttendanceStrategy(AttendanceStrategyFactory.getStrategy("Contractual_Employee"));
          }
     }, [employeeType]);

     const getDaysInMonth = () => {
          const startDate = new Date(monthRange.start);
          return new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
     };

     const getStartDayOfWeek = () => {
          const startDate = new Date(monthRange.start);
          return startDate.getDay();
     };

     // Calculate working hours in HH:MM format (without seconds)
     const calculateWorkingHours = (punchIn, punchOut) => {
          if (!punchIn || !punchOut) return '00:00';
          const punchInDate = new Date(punchIn);
          const punchOutDate = new Date(punchOut);
          const diffMs = punchOutDate - punchInDate;
          if (diffMs <= 0) return '00:00';
          const totalMinutes = Math.floor(diffMs / (1000 * 60));
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
     };

     // Format working hours to HH:MM (remove seconds if present)
     const formatWorkingHours = (workHours) => {
          if (!workHours || workHours === '-') return '-';
          // If it contains seconds (HH:MM:SS), take only HH:MM
          const parts = workHours.split(':');
          if (parts.length >= 2) {
               return `${parts[0]}:${parts[1]}`;
          }
          return workHours;
     };

     // format schedule/punch times to HH:MM
     const formatTimeValue = (timeStr) => {
          if (!timeStr) return '-';
          const parts = String(timeStr).split(':');
          return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : String(timeStr);
     };

     // robust formatter for timestamps (ISO / "YYYY-MM-DD HH:MM:SS" / "HH:MM:SS")
     const formatAnyTime = (time) => {
          if (!time) return '-';
          const s = String(time).trim();
          if (s.includes('T') || s.includes(' ')) {
               const parsed = new Date(s);
               if (!isNaN(parsed)) {
                    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
               }
          }
          return formatTimeValue(s);
     };

     const getShiftPunchIn = (shift) => {
          if (!shift) return null;
          return shift.punch_in || shift.punchIn || shift.punch_in_time || shift.in_time_actual || null;
     };
     const getShiftPunchOut = (shift) => {
          if (!shift) return null;
          return shift.punch_out || shift.punchOut || shift.punch_out_time || shift.out_time_actual || null;
     };

     const secondsToHM = (totalSeconds) => {
          if (!totalSeconds) return "00:00";
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
     };
     // Prepare holiday map
     const holidayDatesMap = {};
     holidays.forEach(h => {
          holidayDatesMap[h.holiday_date] = h.holiday_name;
     });

     useEffect(() => {
          const daysInMonth = getDaysInMonth();
          let sundayCounter = 0;
          let holidayCounter = 0;

          for (let d = 1; d <= daysInMonth; d++) {
               const year = new Date(monthRange.start).getFullYear();
               const month = new Date(monthRange.start).getMonth() + 1;
               const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
               const dateObj = new Date(year, month - 1, d);

               const dayStatus = days.find(day => {
                    const dayDate = new Date(day.date);
                    return (
                         dayDate.getFullYear() === year &&
                         dayDate.getMonth() + 1 === month &&
                         dayDate.getDate() === d
                    );
               });

               if (!dayStatus) {
                    if (dateObj.getDay() === weekOffDayNumber) {
                         sundayCounter++;
                    } else if (holidayDatesMap[dateStr]) {
                         holidayCounter++;
                    }
               }
          }

          setSundaysCount(sundayCounter);
          setHolidaysCount(holidayCounter);
     }, [monthRange, days, holidays]);

     const calculateAttendanceStatus = (day, roster, holidays) => {
          if (!attendanceStrategy) {
               const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
               return {
                    status: day.punch_in ? "Present" : "Absent",
                    lateMark: "-",
                    workingHours: workHours,
                    statusColor: day.punch_in ? "#10b981" : "#ef4444",
                    remarks: ""
               };
          }
          try {
               const employeeData = {
                    weekAttendance: [weekOffDay],
                    allDays: days,
                    region: "Other"
               };
               const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, employeeData);

               // Format working hours to HH:MM
               if (result.workingHours && result.workingHours !== '-') {
                    result.workingHours = formatWorkingHours(result.workingHours);
               }

               // APPLY REGULARIZE OVERRIDE: if approved regularize entries exist, recompute status similarly to main attendance view
               try {
                    const approvedRegs = Array.isArray(day.regularize) ? day.regularize.filter(r => (r.status || '').toLowerCase() === 'approved') : [];
                    if (approvedRegs.length > 0) {
                         // sum durations from all approved regularize entries that have both times
                         let totalRegSeconds = 0;
                         approvedRegs.forEach(r => {
                              const pin = r.punch_in || r.punchIn || null;
                              const pout = r.punch_out || r.punchOut || null;
                              if (pin && pout) {
                                   let inDate = pin.includes('-') || pin.includes('T') ? new Date(pin) : new Date(day.date + ' ' + pin);
                                   let outDate = pout.includes('-') || pout.includes('T') ? new Date(pout) : new Date(day.date + ' ' + pout);
                                   if (inDate && outDate && outDate > inDate) {
                                        totalRegSeconds += Math.floor((outDate - inDate) / 1000);
                                   }
                              }
                         });

                         if (totalRegSeconds > 0) {
                              const shiftHours = parseFloat(roster?.total_hours) || 8;
                              const halfThresholdHours = parseFloat(roster?.WorkingsHoursToBeConsiderdHalfDay) || (shiftHours / 2);

                              const shiftSeconds = Math.floor(shiftHours * 3600);
                              const halfSeconds = Math.floor(halfThresholdHours * 3600);

                              // determine earliest reg in time to compute reg late
                              let regLateMark = '-';
                              try {
                                   const regIns = approvedRegs.map(r => r.punch_in || r.punchIn).filter(Boolean);
                                   if (regIns.length && roster && roster.in_time) {
                                        const earliestRegIn = regIns.map(pin => (pin.includes('-') || pin.includes('T')) ? new Date(pin) : new Date(day.date + ' ' + pin)).reduce((a, b) => a < b ? a : b);
                                        const rosterIn = roster.in_time ? new Date(day.date + ' ' + roster.in_time) : null;
                                        let allowedIn = rosterIn;
                                        if (roster && roster.grace_in) {
                                             allowedIn = new Date(allowedIn.getTime() + (parseFloat(roster.grace_in || 0) * 60 * 1000));
                                        }
                                        if (allowedIn && earliestRegIn && earliestRegIn > allowedIn) regLateMark = 'Late';
                                   }
                              } catch (e) {
                                   console.warn('Failed to compute reg late mark', e);
                                   regLateMark = '-';
                              }

                              const regWorking = secondsToHM(totalRegSeconds);

                              if (totalRegSeconds >= shiftSeconds) {
                                   return {
                                        status: 'Present',
                                        lateMark: result.lateMark || '-',
                                        workingHours: regWorking,
                                        statusColor: '#10b981',
                                        remarks: 'Present via regularize',
                                        regWorkingHours: regWorking,
                                        regLateMark: regLateMark
                                   };
                              } else if (totalRegSeconds >= halfSeconds) {
                                   return {
                                        status: 'Half Day',
                                        lateMark: result.lateMark || '-',
                                        workingHours: regWorking,
                                        statusColor: '#f59e0b',
                                        remarks: 'Half day via regularize',
                                        regWorkingHours: regWorking,
                                        regLateMark: regLateMark
                                   };
                              } else {
                                   return {
                                        status: 'LOP',
                                        lateMark: result.lateMark || '-',
                                        workingHours: regWorking,
                                        statusColor: '#ef4444',
                                        remarks: 'LOP (regularize insufficient)',
                                        regWorkingHours: regWorking,
                                        regLateMark: regLateMark
                                   };
                              }
                         }
                    }
               } catch (e) {
                    console.error('Error processing regularize override in calendar:', e);
               }

               return result;
          } catch {
               const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
               return {
                    status: day.punch_in ? "Present" : "Absent",
                    lateMark: "-",
                    workingHours: workHours,
                    statusColor: day.punch_in ? "#10b981" : "#ef4444",
                    remarks: ""
               };
          }
     };


     // const renderCalendar = () => {
     //      const daysInMonth = getDaysInMonth();
     //      const startDayOfWeek = getStartDayOfWeek();
     //      const totalCells = 42;
     //      const calendar = [];
     //      const today = new Date();
     //      const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

     //      for (let i = 0; i < totalCells; i++) {
     //           const currentDay = i - startDayOfWeek + 1;
     //           let punchIn = null;
     //           let punchOut = null;
     //           let holidayName = null;
     //           let workingHours = null;
     //           let status = null;
     //           let statusColor = null;
     //           let shiftInTime = null;
     //           let shiftOutTime = null;

     //           if (currentDay > 0 && currentDay <= daysInMonth) {
     //                const year = new Date(monthRange.start).getFullYear();
     //                const month = new Date(monthRange.start).getMonth() + 1;
     //                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;
     //                const dayStatus = days.find(day => day.date === dateStr);
     //                const roster = attendanceSummary?.duty_roster?.find(r => r.attendance_date === dateStr);
     //                const holidayObj = holidays.find(h => h.holiday_date === dateStr);
     //                holidayName = holidayObj?.holiday_name;

     //                // Get shift times from roster
     //                if (roster) {
     //                     shiftInTime = roster.in_time;
     //                     shiftOutTime = roster.out_time;
     //                }

     //                const attendanceResult = calculateAttendanceStatus(
     //                     dayStatus || { date: dateStr, punch_in: null, punch_out: null },
     //                     roster,
     //                     holidays
     //                );
     //                status = attendanceResult.status;
     //                statusColor = attendanceResult.statusColor;
     //                workingHours = attendanceResult.workingHours;
     //                punchIn = dayStatus?.punch_in;
     //                punchOut = dayStatus?.punch_out;

     //                // ✅ Fix: Show "OnDuty" for current date if:
     //                // 1. punch_out is null/empty, OR
     //                // 2. punch_in and punch_out are the same
     //                if (dateStr === currentDate && punchIn) {
     //                     if (!punchOut || punchIn === punchOut) {
     //                          status = "OnDuty";
     //                          statusColor = "#3b82f6"; // Blue color for OnDuty
     //                     }
     //                }
     //           }

     //           const isEmptyCell = currentDay <= 0 || currentDay > daysInMonth;

     //           calendar.push(
     //                <div
     //                     key={i}
     //                     className={`calendar-day ${isEmptyCell ? 'empty-day' : ''}`}
     //                     onMouseEnter={(e) => !isEmptyCell && handleMouseEnter(e, currentDay, punchIn, punchOut, workingHours, shiftInTime, shiftOutTime)}
     //                     onMouseLeave={handleMouseLeave}
     //                >
     //                     {!isEmptyCell && (
     //                          <>
     //                               {status && (
     //                                    <span
     //                                         className="status-dot"
     //                                         style={{
     //                                              backgroundColor: statusColor || (
     //                                                   status === "present" ? "green" :
     //                                                        status === "absent" ? "red" :
     //                                                             status === "leave" ? "blue" :
     //                                                                  status === "holiday" ? "#FFBF00" :
     //                                                                       status === "week_off" ? "#00BFFC" :
     //                                                                            status === "Half Day" ? "orange" :
     //                                                                                 status === "Early Going" ? "purple" :
     //                                                                                      status === "OnDuty" ? "#3b82f6" : "transparent"
     //                                              )
     //                                         }}
     //                                    />
     //                               )}

     //                               <span className="day-number">{currentDay}</span>

     //                               {holidayName && (
     //                                    <div className="holiday-name">{holidayName}</div>
     //                               )}

     //                               <div className="day-details">
     //                                    {(status === "Week Off" || status === "Weekoff") && (
     //                                         <div className="status-text week-off-text">Weekly Off</div>
     //                                    )}

     //                                    {/* ✅ UPDATED: Show shift times if available and (NOT a week-off OR has attendance on week-off) */}
     //                                    {shiftInTime && shiftOutTime && (
     //                                         (status !== "Week Off" && status !== "Weekoff") ||
     //                                         (punchIn && punchOut) // Show shift time if employee attended even on week-off
     //                                    ) && (
     //                                              <div className="shift-time-info">
     //                                                   <span>🕐 {shiftInTime.substring(0, 5)} - {shiftOutTime.substring(0, 5)}</span>
     //                                              </div>
     //                                         )}

     //                                    {(punchIn || punchOut) && (
     //                                         <div className="time-info-row">
     //                                              {punchIn && <span className="time-in"><FormattedTime value={punchIn} /></span>}
     //                                              {punchIn && punchOut && punchIn !== punchOut && <span className="time-separator">-</span>}
     //                                              {punchOut && punchIn !== punchOut && <span className="time-out"><FormattedTime value={punchOut} /></span>}
     //                                         </div>
     //                                    )}
     //                                    {workingHours && workingHours !== "-" && workingHours !== "00:00" && (
     //                                         <div className="working-hours">
     //                                              {formatWorkingHours(workingHours)} Hrs
     //                                         </div>
     //                                    )}
     //                                    {status && status !== "Present" && status !== "Week Off" && status !== "Weekoff" && !holidayName && status !== "-" && (
     //                                         <div className="status-text">{status}</div>
     //                                    )}
     //                               </div>
     //                          </>
     //                     )}
     //                </div>
     //           );
     //      }

     //      return calendar;
     // };




     const renderCalendar = () => {
          const daysInMonth = getDaysInMonth();
          const startDayOfWeek = getStartDayOfWeek();
          const totalCells = 42;
          const calendar = [];
          const today = new Date();
          const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

          for (let i = 0; i < totalCells; i++) {
               const currentDay = i - startDayOfWeek + 1;
               const isEmptyCell = currentDay <= 0 || currentDay > daysInMonth;


               if (isEmptyCell) {
                    calendar.push(<div key={i} className="calendar-day empty-day"></div>);
                    continue;
               }

               const year = new Date(monthRange.start).getFullYear();
               const month = new Date(monthRange.start).getMonth() + 1;
               const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

               const cellDate = new Date(year, month - 1, currentDay);
               const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
               const isUpcomingDate = cellDate > todayDate;

               const dayStatus = days.find(day => day.date === dateStr);
               const roster = attendanceSummary?.duty_roster?.find(r => r.attendance_date === dateStr);
               const holidayObj = holidays.find(h => h.holiday_date === dateStr);
               const holidayName = holidayObj?.holiday_name;

               let shiftInTime = roster?.in_time || null;
               let shiftOutTime = roster?.out_time || null;

               const attendanceResult = calculateAttendanceStatus(dayStatus || { date: dateStr, punch_in: null, punch_out: null, shifts: [] }, roster, holidays);
               let status = attendanceResult.status;
               const statusColor = attendanceResult.statusColor;
               const workingHours = attendanceResult.workingHours;
               const punchIn = dayStatus?.punch_in;
               const punchOut = dayStatus?.punch_out;
               let isWeekOff = status === "Week Off" || status === "Weekoff";
               const isSunday = new Date(dateStr).getDay() === 0;
               const splitShiftFlag = attendanceSummary?.employee_info?.split_shift || 'N';
               const isDoctorSplit = String(employeeType).toUpperCase() === 'DOCTOR' && splitShiftFlag === 'Y' && Array.isArray(dayStatus?.shifts);
               // OnDuty handling for current date
               if (dateStr === currentDate && punchIn && (!punchOut || punchIn === punchOut)) {
                    status = "OnDuty";
               }

               if (isDoctorSplit) {
                    const shift1 = (dayStatus?.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
                    const shift2 = (dayStatus?.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};
                    const schedS1In = formatTimeValue(shift1.in_time);
                    const schedS1Out = formatTimeValue(shift1.out_time);
                    const schedS2In = formatTimeValue(shift2.in_time);
                    const schedS2Out = formatTimeValue(shift2.out_time);
                    const punchS1In = getShiftPunchIn(shift1);
                    const punchS1Out = getShiftPunchOut(shift1);
                    const punchS2In = getShiftPunchIn(shift2);
                    const punchS2Out = getShiftPunchOut(shift2);
                    const displayPunchS1In = punchS1In ? formatAnyTime(punchS1In) : '-';
                    const displayPunchS1Out = punchS1Out ? formatAnyTime(punchS1Out) : '-';
                    const displayPunchS2In = punchS2In ? formatAnyTime(punchS2In) : '-';
                    const displayPunchS2Out = punchS2Out ? formatAnyTime(punchS2Out) : '-';
                    const totalWorkedSeconds = (shift1.worked_seconds || 0) + (shift2.worked_seconds || 0);

                    const mouseEnter = !isWeekOff ? (e) => handleMouseEnter(e, currentDay, null, null, null, null, null, { shift1, shift2 }) : () => { };

                    calendar.push(
                         <div
                              key={i}
                              className={`calendar-day ${isWeekOff ? 'weekoff-day' : ''} ${isSunday ? 'sunday-day' : ''} ${isUpcomingDate ? 'upcoming-date' : ''}`}
                              onMouseEnter={mouseEnter}
                              onClick={(e) => handleCellClick(e, dateStr, currentDay)}
                              style={{ cursor: 'pointer' }}
                              onMouseLeave={handleMouseLeave}
                         >
                              {status && status !== "-" && <span className="status-dot" style={{ backgroundColor: statusColor }} />
                              }
                              <span className="day-number">{currentDay}</span>
                              {holidayName && <div className="holiday-name">{holidayName}</div>
                              }
                              <div className="day-details-split">
                                   {isWeekOff ? (
                                        <div className="status-text week-off-text">Weekly Off</div>
                                   ) : (
                                        <>
                                             <div className="row">
                                                  <div className="col-12 col-sm-6">
                                                       <div className="shift-label">S1</div>
                                                       <div className="shift-punch-block">
                                                            <div className="shift-punch schedule">{schedS1In} / {schedS1Out}</div>
                                                            <div className="shift-punches punch" style={{ marginTop: '4px', fontWeight: 700, fontSize: 12, color: (punchS1In || punchS1Out) ? '#10b981' : '#6b7280' }}>
                                                                 {displayPunchS1In} / {displayPunchS1Out}
                                                            </div>
                                                       </div>
                                                  </div>
                                                  <div className="col-12 col-sm-6">
                                                       <div className="shift-label">S2</div>
                                                       <div className="shift-punch-block">
                                                            <div className="shift-punch schedule">{schedS2In} / {schedS2Out}</div>
                                                            <div className="shift-punches punch" style={{ marginTop: '4px', fontWeight: 700, fontSize: 12, color: (punchS2In || punchS2Out) ? '#10b981' : '#6b7280' }}>
                                                                 {displayPunchS2In} / {displayPunchS2Out}
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                             <div className="split-summary" style={{ marginTop: 4, fontSize: 12 }}>
                                                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{secondsToHM(totalWorkedSeconds)} Hrs</span>
                                             </div>

                                             {/* Show regularize timings for split-shift days */}
                                             {Array.isArray(dayStatus?.regularize) && dayStatus.regularize.length > 0 && (
                                                  <div style={{ marginTop: 6, fontSize: 11 }}>
                                                       {dayStatus.regularize.map((r) => {
                                                            const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || null;
                                                            const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                            return (
                                                                 <div key={r.id} style={{ color: '#059669', display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                                                                      <span style={{ fontSize: 11 }}>{r.punch_in || r.punchIn ? `Reg In: ${formatAnyTime(r.punch_in || r.punchIn)}` : ''}{r.punch_out || r.punchOut ? ` ${r.punch_in || r.punchIn ? ' / ' : ''}Reg Out: ${formatAnyTime(r.punch_out || r.punchOut)}` : ''}</span>
                                                                      {approver && <span style={{ fontSize: 10, color: '#64748b' }}>By: {approver}</span>}
                                                                      {attCount > 0 && <span style={{ fontSize: 10, color: '#64748b' }}>📎{attCount}</span>}
                                                                 </div>
                                                            );
                                                       })}
                                                  </div>
                                             )}

                                             {/* Regularize indicator for split-shift days */}
                                             {Array.isArray(dayStatus?.regularize) && dayStatus.regularize.length > 0 && (
                                                  (() => {
                                                       const regs = dayStatus.regularize;
                                                       const anyPending = regs.some(r => (r.status || '').toLowerCase() !== 'approved');
                                                       const badgeColor = anyPending ? '#f59e0b' : '#10b981';
                                                       return (
                                                            <div style={{ marginTop: 6 }}>
                                                                 <span style={{ background: badgeColor, color: '#fff', padding: '4px 6px', borderRadius: 6, fontSize: 11 }} title={regs.map(r => `${r.for_date || r.forDate || r.date} - ${r.status || r.reason || ''}${(r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName) ? ' - By: ' + (r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName) : ''}${Array.isArray(r.attachments) ? ' - Attachments: ' + r.attachments.length : (Array.isArray(r.attachment_ids) ? ' - Attachments: ' + r.attachment_ids.length : '')}`).join('\n')}>
                                                                      Reg: {regs.length}
                                                                 </span>
                                                            </div>
                                                       );
                                                  })()
                                             )}
                                        </>
                                   )}
                              </div>
                         </div >
                    );
               } else {
                    calendar.push(
                         <div
                              key={i}
                              className={`calendar-day ${isWeekOff ? 'weekoff-day' : ''} ${isUpcomingDate ? 'upcoming-date' : ''} ${isSunday ? 'sunday-day' : ''}`}
                              onMouseEnter={(e) => handleMouseEnter(e, currentDay, punchIn, punchOut, workingHours, shiftInTime, shiftOutTime)}
                              onClick={(e) => handleCellClick(e, dateStr, currentDay)}
                              style={{ cursor: 'pointer' }}
                              onMouseLeave={handleMouseLeave}
                         >
                              {status && status !== "-" && <span className="status-dot" style={{ backgroundColor: statusColor }} />}
                              <span className="day-number">{currentDay}</span>
                              {holidayName && <div className="holiday-name">{holidayName}</div>}
                              <div className="day-details">
                                   {isWeekOff ? <div className="status-text week-off-text">Weekly Off</div> : null}
                                   {shiftInTime && shiftOutTime && (!isWeekOff || (punchIn && punchOut)) && (
                                        <div className="shift-time-info">
                                             <span>🕐 {shiftInTime.substring(0, 5)} - {shiftOutTime.substring(0, 5)}</span>
                                        </div>
                                   )}
                                   {(punchIn || punchOut) && (
                                        <div className="time-info-row">
                                             {punchIn && <span className="time-in"><FormattedTime value={punchIn} /></span>}
                                             {punchIn && punchOut && punchIn !== punchOut && <span className="time-separator">-</span>}
                                             {punchOut && punchIn !== punchOut && <span className="time-out"><FormattedTime value={punchOut} /></span>}
                                        </div>
                                   )}
                                   {workingHours && workingHours !== "-" && workingHours !== "00:00" && <div className="working-hours">{formatWorkingHours(workingHours)} Hrs</div>}
                                   {status && status !== "Present" && status !== "Week Off" && status !== "Weekoff" && !holidayName && status !== "-" && <div className="status-text">{status}</div>}

                                   {/* Show regularize timings for normal days */}
                                   {Array.isArray(dayStatus?.regularize) && dayStatus.regularize.length > 0 && (
                                        <div style={{ marginTop: 6, fontSize: 11 }}>
                                             {dayStatus.regularize.map((r) => {
                                                  const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || null;
                                                  const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                  return (
                                                       <div key={r.id} style={{ color: '#059669', display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                                                            <span style={{ fontSize: 11 }}>{r.punch_in || r.punchIn ? `Reg In: ${formatAnyTime(r.punch_in || r.punchIn)}` : ''}{r.punch_out || r.punchOut ? ` ${r.punch_in || r.punchIn ? ' / ' : ''}Reg Out: ${formatAnyTime(r.punch_out || r.punchOut)}` : ''}</span>
                                                            {approver && <span style={{ fontSize: 10, color: '#64748b' }}>By: {approver}</span>}
                                                            {attCount > 0 && <span style={{ fontSize: 10, color: '#64748b' }}>📎{attCount}</span>}
                                                       </div>
                                                  );
                                             })}
                                        </div>
                                   )}

                                   {/* Regularize indicator for normal days */}
                                   {Array.isArray(dayStatus?.regularize) && dayStatus.regularize.length > 0 && (
                                        (() => {
                                             const regs = dayStatus.regularize;
                                             const anyPending = regs.some(r => (r.status || '').toLowerCase() !== 'approved');
                                             const badgeColor = anyPending ? '#f59e0b' : '#10b981';
                                             return (
                                                  <div style={{ marginTop: 6 }}>
                                                       <span style={{ background: badgeColor, color: '#fff', padding: '4px 6px', borderRadius: 6, fontSize: 11 }} title={regs.map(r => `${r.for_date || r.forDate || r.date} - ${r.status || r.reason || ''}${(r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName) ? ' - By: ' + (r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName) : ''}${Array.isArray(r.attachments) ? ' - Attachments: ' + r.attachments.length : (Array.isArray(r.attachment_ids) ? ' - Attachments: ' + r.attachment_ids.length : '')}`).join('\n')}>
                                                            Reg: {regs.length}
                                                       </span>
                                                  </div>
                                             );
                                        })()
                                   )}
                              </div>
                         </div >
                    );
               }
          }

          return calendar;
     };
     // handleMouseEnter / handleMouseLeave must be inside component scope
     const handleMouseEnter = (e, currentDay, punchIn, punchOut, workingHours, shiftInTime, shiftOutTime, shiftObj) => {
          const position = e.target.getBoundingClientRect();

          // split-shift object provided (shiftObj: { shift1, shift2 })
          if (shiftObj && (shiftObj.shift1 || shiftObj.shift2)) {
               const s1 = shiftObj.shift1 || {};
               const s2 = shiftObj.shift2 || {};
               const totalWorkedSeconds = (s1.worked_seconds || 0) + (s2.worked_seconds || 0);
               setPopupDetails({
                    show: true,
                    punchIn: getShiftPunchIn(s1) || null,
                    punchOut: getShiftPunchOut(s2) || null,
                    day: currentDay,
                    workHours: secondsToHM(totalWorkedSeconds),
                    position: {
                         top: position.bottom + window.scrollY + 5,
                         left: position.left + window.scrollX,
                    },
                    shift1: s1,
                    shift2: s2
               });
               return;
          }

          // single punch flow
          if (punchIn && punchOut) {
               const formattedHours = workingHours || calculateWorkingHours(punchIn, punchOut);
               setPopupDetails({
                    show: true,
                    punchIn,
                    punchOut,
                    day: currentDay,
                    workHours: formatWorkingHours(formattedHours),
                    position: {
                         top: position.bottom + window.scrollY + 5,
                         left: position.left + window.scrollX,
                    }
               });
          }
     };


     const handleMouseLeave = () => {
          setPopupDetails({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     };

     const handleMonthChange = (e) => {
          const newMonth = e.target.value;
          setSelectedMonth(newMonth);
          if (onMonthChange) {
               const [year, month] = newMonth.split('-');
               onMonthChange(new Date(Number(year), Number(month) - 1, 1));
          }
     };

     const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

     const getOptionsModalStyle = () => {
          if (!optionsModal?.position) return {};
          let { top = 0, left = 0 } = optionsModal.position;
          const estimateH = 160;
          const estimateW = 320;
          const viewportTop = window.scrollY;
          const viewportBottom = viewportTop + window.innerHeight;
          const viewportRight = window.scrollX + window.innerWidth;

          if (top + estimateH > viewportBottom) {
               top = Math.max(viewportTop + 8, top - estimateH - 10);
          }
          if (left + estimateW > viewportRight) {
               left = Math.max(window.scrollX + 8, viewportRight - estimateW - 10);
          }
          return { top, left, position: 'absolute', zIndex: 1050, minWidth: 220 };
     };

     return (
          <div className="attendance-calendar-wrapper">
               <div className="calendar-header">
                    <input
                         type="month"
                         className="month-selector"
                         value={selectedMonth}
                         onChange={handleMonthChange}
                         min="2025-08"
                    />
               </div>

               <div className="calendar-container">
                    <div className="calendar-weekdays">
                         {daysOfWeek.map((day, index) => (
                              <div key={index} className="weekday-header">
                                   {day}
                              </div>
                         ))}
                    </div>
                    <div className="calendar-grid">
                         {renderCalendar()}
                    </div>
               </div>

               {popupDetails.show && (
                    <div
                         className="attendance-popup"
                         style={{
                              top: popupDetails.position.top,
                              left: popupDetails.position.left,
                         }}
                    >
                         {popupDetails.shift1 || popupDetails.shift2 ? (
                              <>
                                   <div className="popup-time">S1 Sch: {popupDetails.shift1 ? formatTimeValue(popupDetails.shift1.in_time) + ' / ' + formatTimeValue(popupDetails.shift1.out_time) : '-'}</div>
                                   <div className="popup-time">S1 Punch: {popupDetails.shift1 ? formatAnyTime(getShiftPunchIn(popupDetails.shift1)) + ' / ' + formatAnyTime(getShiftPunchOut(popupDetails.shift1)) : '-'}</div>
                                   <div style={{ height: 6 }} />
                                   <div className="popup-time">S2 Sch: {popupDetails.shift2 ? formatTimeValue(popupDetails.shift2.in_time) + ' / ' + formatTimeValue(popupDetails.shift2.out_time) : '-'}</div>
                                   <div className="popup-time">S2 Punch: {popupDetails.shift2 ? formatAnyTime(getShiftPunchIn(popupDetails.shift2)) + ' / ' + formatAnyTime(getShiftPunchOut(popupDetails.shift2)) : '-'}</div>
                                   <div className="popup-hours">Total: {popupDetails.workHours} Hrs</div>
                              </>
                         ) : (
                              <>
                                   <span style={{ fontSize: '12px', color: 'blue' }}><FormattedTime value={popupDetails.punchIn} /></span>
                                   <span style={{ fontSize: '12px', color: 'hsl(0, 100%, 50%)' }}><FormattedTime value={popupDetails.punchOut} /></span>
                                   <span style={{ fontSize: '12px', color: '#00491e' }}>{popupDetails.workHours} Hrs</span>
                              </>
                         )}
                    </div>
               )
               }



               {optionsModal.show && (
                    <>
                         {/* Bootstrap backdrop */}
                         <div className="modal-backdrop show" onClick={closeOptionsModal} />
                         <div
                              className="options-popover card shadow-sm"
                              role="dialog"
                              aria-modal="true"
                              onClick={(e) => e.stopPropagation()}
                              style={getOptionsModalStyle()}
                              tabIndex={-1}
                         >
                              <div className="card-body p-2">
                                   <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                             <div className="fw-bold">Actions</div>
                                             <div className="text-muted small">{optionsModal.dateStr}</div>
                                        </div>
                                        <button type="button" className="btn-close" aria-label="Close" onClick={closeOptionsModal} />
                                   </div>

                                   {/* <div className="d-flex flex-wrap gap-2">
                                        <button className="btn btn-success btn-sm" onClick={handleRegularization}>
                                             Regularization Request
                                        </button>
                                        <button className="btn btn-primary btn-sm" onClick={handleApplyLeave}>
                                             Apply Leave
                                        </button>

                                   </div> */}
                              </div>
                         </div>
                    </>
               )
               }

               <div className="calendar-legend">
                    <div className="legend-item present">
                         <span className="legend-dot"></span>
                         Present: {attendanceSummary.present}
                    </div>
                    <div className="legend-item absent">
                         <span className="legend-dot"></span>
                         Absent: {attendanceSummary.absent}
                    </div>
                    <div className="legend-item week-off">
                         <span className="legend-dot"></span>
                         Weekly Off: {sundaysCount}
                    </div>
                    <div className="legend-item holiday">
                         <span className="legend-dot"></span>
                         Holiday: {attendanceSummary.holiday}
                    </div>
               </div>
          </div>
     );
};

export default AttendanceCalendar;